import React, { useState, useEffect } from 'react';
import { DatePicker as AntDatePicker } from 'antd';
import moment from 'moment';
import isString from 'lodash/isString';
import { validMomentFormat } from 'utils/helpers';
import { Label } from 'components';
import { ReactComponent as Calendar } from 'assets/icons/calendar.gov.svg';
import { dateFormatList } from 'utils';

const DatePicker = ({
  label,
  value,
  asterisk,
  allowClear = false,
  disabledDate,
  ...rest
}) => {
  const [inputValue, setInputValue] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [openPopover, setOpenPopover] = useState(false);
  const [userFocus, setUserFocus] = useState(false);
  const [recentlyValidated, setRecentlyValidated] = useState(false);
  
  // Create ref to directly access the DatePicker input element
  const datePickerRef = React.useRef(null);

  // Define valid date formats
  const validFormats = [
    'DD/MM/YYYY', 'DD-MM-YYYY', 'DD.MM.YYYY',
    'D/M/YYYY', 'D-M-YYYY', 'D.M.YYYY',
    moment.ISO_8601
  ];

  useEffect(() => {
    if (value) {
      const formattedValue = moment(value).isValid()
        ? moment(value).format('DD/MM/YYYY')
        : '';
      setInputValue(formattedValue);
      validateInput(formattedValue);
    }
  }, [value]);

  // Additional check for valid date (checks if date like 31/04/2023 is valid)
  const isRealDate = (day, month, year) => {
    // Create a date with the values and check if the date's components match what we put in
    // This catches invalid dates like 31/04/2023 (April doesn't have 31 days)
    const date = new Date(year, month - 1, day);
    return date.getFullYear() === year && 
           date.getMonth() === month - 1 && 
           date.getDate() === day;
  };

  const validateInput = input => {
    setErrorMessage('');

    if (!input) {
      // Different required message based on context
      setErrorMessage(
        rest.name === 'providerReturnDate'
          ? 'Η ημερομηνία είναι υποχρεωτική.'
          : 'Η Ημ. Γέννησης είναι απαραίτητη.'
      );
      return false;
    }

    // Support multiple separators (/, -, .)
    const parts = input.split(/[-/\.]/);

    if (!parts[0] || !parts[1] || !parts[2]) {
      setErrorMessage('Η ημερομηνία πρέπει να είναι πλήρης (ΗΗ/ΜΜ/ΕΕΕΕ).');
      return false;
    }

    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    const year = parseInt(parts[2], 10);

    // Basic date part validation
    if (isNaN(day) || day < 1 || day > 31) {
      setErrorMessage('Η ημέρα πρέπει να είναι μεταξύ 1 και 31.');
      return false;
    }

    if (isNaN(month) || month < 1 || month > 12) {
      setErrorMessage('Ο μήνας πρέπει να είναι μεταξύ 1 και 12.');
      return false;
    }

    // Check if the date is actually a real calendar date
    if (!isRealDate(day, month, year)) {
      setErrorMessage('Η ημερομηνία δεν είναι έγκυρη. Ελέγξτε τον αριθμό ημερών για το συγκεκριμένο μήνα.');
      return false;
    }

    // Different year validation based on context
    if (rest.name === 'providerReturnDate') {
      const currentDate = moment();
      if (isNaN(year) || year < currentDate.year() || year > 2027) {
        setErrorMessage(
          'Η ημερομηνία πρέπει να είναι μεταξύ του τρέχοντος έτους και 2027.'
        );
        return false;
      }
    } else {
      // Birth date validation
      if (isNaN(year) || year < 1900 || year > 2010) {
        setErrorMessage('Η ημερομηνία δεν είναι έγκυρη για το σύστημα.');
        return false;
      }
    }

    // Try parsing with multiple formats
    let isValidDate = false;
    let parsedDate;
    
    for (const format of validFormats) {
      parsedDate = moment(input, format, true);
      if (parsedDate.isValid()) {
        isValidDate = true;
        break;
      }
    }
    
    if (!isValidDate) {
      setErrorMessage('Η ημερομηνία δεν είναι έγκυρη.');
      return false;
    }

    return true;
  };

  // Helper function to validate and reset if needed - using useCallback to memoize
  const validateAndResetIfNeeded = React.useCallback(() => {
    if (recentlyValidated) return; // Skip if we just validated
    
    setRecentlyValidated(true);
    if (inputValue) {
      if (!validateInput(inputValue)) {
        // Invalid date - reset the field
        setInputValue('');
        rest.onChange && rest.onChange(null);
      }
    }
    
    // Reset the flag after a short delay
    setTimeout(() => setRecentlyValidated(false), 100);
  }, [inputValue, recentlyValidated, rest.onChange]);

  const handleManualInput = event => {
    const input = event.target.value;
    setInputValue(input);

    // Only validate when we have a full date format (at least 8 characters)
    if (input.length >= 8) {
      if (validateInput(input)) {
        // Try all formats to parse the date
        let validDate;
        for (const format of validFormats) {
          const parsedDate = moment(input, format, true);
          if (parsedDate.isValid()) {
            validDate = parsedDate;
            break;
          }
        }
        
        if (validDate) {
          rest.onChange && rest.onChange(validDate.format('YYYY-MM-DD'));
        }
      } else {
        // We have a validation error, notify parent component
        rest.onChange && rest.onChange(null);
      }
    } else if (input === '') {
      // Clear the field
      setErrorMessage('Το πεδίο είναι υποχρεωτικό.');
      rest.onChange && rest.onChange(null);
    }
  };

  const handleDatePickerChange = (date, dateString) => {
    if (date) {
      const formattedDate = moment(date).format('DD/MM/YYYY');
      
      // Extract date parts to verify it's a real date
      const parts = formattedDate.split('/');
      const day = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10);
      const year = parseInt(parts[2], 10);
      
      if (isRealDate(day, month, year) && validateInput(formattedDate)) {
        setInputValue(formattedDate);
        rest.onChange && rest.onChange(moment(date).format('YYYY-MM-DD'));
      } else {
        // Invalid date selected - clear field and show error
        setInputValue('');
        if (!errorMessage) {
          setErrorMessage('Η ημερομηνία δεν είναι έγκυρη.');
        }
        rest.onChange && rest.onChange(null);
      }
    } else {
      setInputValue('');
      setErrorMessage('Το πεδίο είναι υποχρεωτικό.');
      rest.onChange && rest.onChange(null);
    }
  };

  const handleBlur = () => {
    validateAndResetIfNeeded();
  };

  const handleKeyDown = (e) => {
    // Handle space key to open the calendar
    if (e.key === ' ') {
      e.preventDefault();
      setOpenPopover(true);
    }
    
    // Handle Enter key to validate and reset if invalid
    if (e.key === 'Enter') {
      e.preventDefault();
      validateAndResetIfNeeded();
    }
  };

  // Add effect to handle direct blur events
  useEffect(() => {
    const timer = setTimeout(() => {
      if (datePickerRef.current) {
        const inputElement = datePickerRef.current.querySelector('input');
        if (inputElement) {
          inputElement.addEventListener('blur', validateAndResetIfNeeded);
          return () => {
            inputElement.removeEventListener('blur', validateAndResetIfNeeded);
          };
        }
      }
    }, 100); // Short delay to ensure DOM is ready
    
    return () => clearTimeout(timer);
  }, [validateAndResetIfNeeded]);

  return (
    <>
      <Label label={label} asterisk={asterisk} />
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }} ref={datePickerRef}>
        <AntDatePicker
          {...rest}
          value={inputValue ? moment(inputValue, 'DD/MM/YYYY', true) : null}
          format={dateFormatList}
          allowClear={allowClear}
          suffixIcon={<Calendar className="ant-calendar-icon" />}
          inputReadOnly={false}
          onChange={handleDatePickerChange}
          onBlur={handleBlur}
          onInput={e => handleManualInput(e)}
          onKeyDown={handleKeyDown}
          style={{ 
            width: '100%',
            borderColor: errorMessage ? '#ff4d4f' : undefined
          }}
          className={errorMessage ? 'date-picker-error' : ''}
          defaultPickerValue={
            !rest.name?.includes('providerReturnDate')
              ? moment('2010-12-31')
              : undefined
          }
          disabledDate={d => {
            if (!d) return true;
            if (rest.name === 'providerReturnDate') {
              return d.year() < moment().year() || d.year() > 2027;
            } 
            return (d.year() < 1900 || d.year() > 2010) && disabledDate;
          }}
          onClick={() => setUserFocus(true)}
          onOpenChange={open => {
            if (open && !userFocus) return;
            setOpenPopover(open);
            setUserFocus(false);
            
            // If the popover is closing, validate the input
            if (!open) {
              validateAndResetIfNeeded();
            }
          }}
        />
      </div>
      {errorMessage && (
        <div className="ant-form-item-explain-error">{errorMessage}</div>
      )}
    </>
  );
};

export default DatePicker;